import React from 'react';
import { PortalLimit } from 'playup-web-core';

import appConfig from '../../../appConfig';
import { setAccountLimits } from '../../../api/limits';
import { mergeApiErrors } from '../../../utils/parseApiError';
import useNotify from '../../../hooks/useNotify';

const isUS = appConfig.regions.isUS;

const Limit = ({ afterSubmit, selectedLimit, userLimits, userLimitsRequests }: Props) => {
	const [loading, setLoading] = React.useState(false);
	const isLoadingRef = React.useRef(false);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
	const notify = useNotify();

	const {
		selectedDurations = [],
		activeLimits = [],
		pendingLimits,
	} = React.useMemo(() => {
		const selectedDurations = selectedLimit?.durations.map((duration) => ({
			label: duration.name,
			value: duration.id,
		}));

		const activeLimits = userLimits?.filter((limit) => limit.type.id === selectedLimit?.id) ?? [];
		const pendingLimits = userLimitsRequests?.filter((limit) => limit.type.id === selectedLimit?.id) ?? [];

		return { selectedDurations, selectedLimit, activeLimits, pendingLimits };
	}, [selectedLimit, userLimits, userLimitsRequests]);

	const setLimit = React.useCallback(async (typeId: number, durationId: number, limit: number | null) => {
		if (isLoadingRef.current) return false;

		setErrorMessage(null);
		isLoadingRef.current = true;
		setLoading(isLoadingRef.current);

		try {
			await setAccountLimits(typeId, durationId, limit);

			return true;
		} catch (error: any) {
			if (typeof error === 'string') setErrorMessage(error);
			else if (error instanceof Error) setErrorMessage(error.message);
			else if (error.errors) setErrorMessage(mergeApiErrors(error.errors));
			else setErrorMessage("We couldn't process the request, please try again.");
		} finally {
			isLoadingRef.current = false;
			setLoading(isLoadingRef.current);
		}

		return false;
	}, []);

	const resetErrors = React.useCallback(() => {
		setErrorMessage(null);
	}, []);

	const handleSubmit = async (typeId: number, durationId: number, limit: number | null) => {
		const success = await setLimit(typeId, durationId, limit);
		if (success) {
			notify({
				type: 'success',
				title: 'Success',
				message: 'Your limits have been applied.',
				timeout: 5000,
			});
		}

		afterSubmit();
	};

	const handleRemove = async (typeId: number, durationId: number) => {
		const success = await setLimit(typeId, durationId, null);
		if (success) {
			notify({
				type: 'success',
				title: 'Success',
				message: 'The limit has been removed.',
				timeout: 5000,
			});
		}

		afterSubmit();
	};

	return (
		<PortalLimit
			isLoading={loading}
			activeLimits={activeLimits}
			pendingLimits={pendingLimits}
			description={descriptions[selectedLimit?.id]}
			onRemove={handleRemove}
			onAdd={handleSubmit}
			options={selectedDurations}
			instructions={isUS ? <InstructionsUS /> : <InstructionsAU />}
			errorFeedback={errorMessage}
			name={selectedLimit?.name}
			limitId={selectedLimit?.id}
			resetErrors={resetErrors}
			hasAmount
		/>
	);
};

export default Limit;

interface Props {
	afterSubmit: () => void;
	selectedLimit: PortalLimit;
	userLimits?: Array<PortalUserLimit>;
	userLimitsRequests?: Array<PortalUserLimitRequest>;
}

const descriptions = isUS
	? {
			1: 'Enabling Deposit Limit allows you to exclude yourself from depositing a certain amount for a specific period of time. Once a deposit limit has been set you must wait until your selected duration is over before you can increase or remove your deposit limit.',
			2: 'You can choose to set a maximum total spending amount that you will be able to make in a set time period. If you lower this limit, the change will take effect immediately.',
			3: 'The daily time limit will take place immediately and can be reduced at any time. The daily time limit can be increased or removed after a 24-hour waiting period.',
	  }
	: {
			1: 'Enabling Deposit Limit allows you to place a limit on the amount you can deposit for a specific period of time. If you lower your limit it will also take effect immediately. If you increase your limit, the change will only take effect 7 days after it has been received.',
			2: 'You can choose to set a maximum total spending amount that you will be able to make in a set time period. If you lower this limit, the change will take effect immediately. If you increase your limit, the change will only take effect 7 days after it has been received.',
	  };

const InstructionsUS = () => (
	<>
		<p>
			{appConfig.brand.name} is committed to helping you enjoy our products responsibly. You have the ability to limit
			your own activity on {appConfig.brand.name} in three different ways, outlined below.
		</p>
		<p>
			You may edit your limits at any time. When a new limit is set, it will take effect immediately and renew
			automatically. You may remove or make a limit less restrictive, however it will only become effective once the
			currently selected period is over. If you wish to set a more-restrictive limit, it will take effect immediately.
			If you need assistance in setting or editing limits, please contact Customer Support.
		</p>
	</>
);

const InstructionsAU = () => (
	<>
		<p>
			{appConfig.brand.name} is committed to helping you enjoy our products responsibly. You have the ability to limit
			your own activity on {appConfig.brand.name} in three different ways, outlined below. You may edit your limits at
			any time. When a new limit is set, it will take effect immediately and renew automatically.
		</p>
		<p>
			You may remove or change your limit. If you lower your limit it will also take effect immediately. If you increase
			your limit, the change will only take effect 7 days after it has been received. If you need assistance in setting
			or editing limits, please contact Customer Service.
		</p>
	</>
);
